import React, { useMemo } from 'react';

import { BaseGym, Product } from '../../types/Gym';
import { User } from '../../types/authentication';

import { track } from '../../services/analytics';

import { ProductItem } from './ProductItem';
import { Matrix } from './Matrix';
import { FakeProductItem } from './FakeProductItem';

import { Button, Grid, Row } from '@husslehq/ui-library';

import fakeDoorList from '../../pages/fake-door-list.json';

import styles from '../../components/PassPicker/Product.module.scss';

type Props = {
  products: Product[];
  isTemporarilyClosed?: boolean;
  gym: BaseGym;
  featured: string | string[] | undefined;
  user: User | undefined;
  onClickEdit: () => void;
  productsToShow: string;
};

export const PassPickerProductBlock: React.FC<Props> = ({
  products,
  isTemporarilyClosed,
  gym,
  featured,
  user,
  onClickEdit,
  productsToShow,
}) => {
  const dailyProduct = useMemo(() => products && products.find((product) => product.type === 'daypass'), [products]);
  const monthlyProduct = useMemo(() => products && products.find((product) => product.type === 'monthly'), [products]);
  const directProduct = useMemo(() => products && products.find((product) => product.type === 'direct'), [products]);
  const dailyBullets = [
    { item: 'One visit to your chosen gym' },
    { item: 'Valid for 30 days' },
    { item: 'Buy as a one off or as often as you like' },
  ];
  const monthlyBullets = [
    { item: 'Multi-gym access' },
    { item: 'Unlimited visits' },
    { item: 'No contracts, cancel anytime' },
    { item: 'Fitness apps included' },
  ];
  const directBullets = [
    ...(directProduct && directProduct.original_price && directProduct.original_price > directProduct.price
      ? [{ item: 'Exclusive Hussle joining offer' }]
      : []),
    { item: 'Single gym' },
    { item: 'Unlimited visits' },
    { item: 'Member perks included' },
    { item: '30% off Hussle Day Passes' },
    { item: 'Cancel anytime' },
  ];

  const fakeDoorEntry = fakeDoorList.find((gymEntry) => gymEntry.GymId === parseInt(gym.id, 10));

  const showMatrix = directProduct && monthlyProduct && productsToShow == 'monthly';

  const handleEditClick = () => {
    onClickEdit();
    track({
      event: 'gym_spa',
      category: 'Pass Picker',
      action: 'Edit type of pass',
      label: gym.name,
    });
  };

  return (
    <div>
      {productsToShow !== 'all' && (
        <div className={styles['product__picker-selection-bar']}>
          <div className={styles['product__picker-selection-bar-inner']}>
            <p className={styles['product__picker-selection-text']}>
              You are viewing:{' '}
              <span className={styles['product__picker-selection-pass']}>
                {productsToShow === 'monthly' ? 'Monthly' : 'Day Passes'}
              </span>
            </p>
            <Button className={styles['product__picker-selection-edit']} level="link" onClick={() => handleEditClick()}>
              View all passes
            </Button>
          </div>
        </div>
      )}
      <h2 className={styles['product__picker-title']}>
        {productsToShow !== 'all' ? 'Choose your pass' : 'How would you like to visit?'}
      </h2>
      <Grid>
        {showMatrix && monthlyProduct && directProduct ? (
          <Matrix
            gym={gym}
            monthlyProductId={monthlyProduct.id}
            monthlyPrice={monthlyProduct.price}
            monthlyDiscount={monthlyProduct.discount}
            directProductId={directProduct.id}
            directPrice={directProduct.price}
            directOriginalPrice={directProduct.original_price}
            directRecommended={featured === 'direct'}
            user={user}
          />
        ) : (
          <Row className={styles['product__picker-row']}>
            {monthlyProduct &&
              !directProduct &&
              !!fakeDoorEntry &&
              (productsToShow === 'monthly' || productsToShow === 'all') && (
                <FakeProductItem
                  key="product_direct"
                  type="direct"
                  price={fakeDoorEntry.DirectMembershipPrice * 100}
                  id={monthlyProduct.id}
                  gym={gym}
                  bullets={directBullets}
                  monthlyProduct={monthlyProduct}
                  dailyProduct={dailyProduct}
                  isTemporarilyClosed={isTemporarilyClosed}
                  purchasable={monthlyProduct.purchasable}
                />
              )}
            {directProduct && (productsToShow === 'monthly' || productsToShow === 'all') && (
              <ProductItem
                key={`product_${directProduct.id}`}
                type={directProduct.type}
                price={directProduct.price}
                originalPrice={directProduct.original_price}
                discount={directProduct.discount}
                id={directProduct.id}
                gym={gym}
                bullets={directBullets}
                isTemporarilyClosed={isTemporarilyClosed}
                recommended={featured === 'direct'}
                purchasable={directProduct.purchasable}
                sourceSegments={directProduct.customer_source_segments}
              />
            )}
            {monthlyProduct && (productsToShow === 'monthly' || productsToShow === 'all') && (
              <ProductItem
                key={`product_${monthlyProduct.id}`}
                type={monthlyProduct.type}
                price={monthlyProduct.price}
                discount={monthlyProduct.discount}
                id={monthlyProduct.id}
                gym={gym}
                bullets={monthlyBullets}
                isTemporarilyClosed={isTemporarilyClosed}
                user={user}
                purchasable={monthlyProduct.purchasable}
                sourceSegments={monthlyProduct.customer_source_segments}
              />
            )}
            {dailyProduct && (productsToShow === 'daily' || productsToShow === 'all') && (
              <ProductItem
                key={`product_${dailyProduct.id}`}
                type={dailyProduct.type}
                price={dailyProduct.price}
                id={dailyProduct.id}
                gym={gym}
                bullets={dailyBullets}
                isTemporarilyClosed={isTemporarilyClosed}
                discount={dailyProduct.discount}
                purchasable={dailyProduct.purchasable}
                sourceSegments={dailyProduct.customer_source_segments}
              />
            )}
          </Row>
        )}
      </Grid>
    </div>
  );
};
